import * as React from 'react';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';

import ItemDetails from './ItemDetails';
import ProjectDetails from './ProjectDetails';
import ChatInterface from '../components/chat/ChatInterface';
import DashboardHome from './DashboardHome';
import BulkAiDialog from '../components/dialogs/BulkAiDialog';
import ConfirmDialog from '../components/dialogs/ConfirmDialog';
import NewProjectDialog from '../components/dialogs/NewProjectDialog';
import ProjectDangerDialog from '../components/dialogs/ProjectDangerDialog';
import { requirementsApi } from '../services/api';
import { iamApi } from '../services/iamApi';
import { saveWithConflictPrompt } from '../utils/conflict';
import { downloadTextFile } from '../utils/exportChat';
import { shouldSuppressTreeShortcut } from '../utils/keyboardGuards';
import { computeWatchedProjectIds, mergeOpenCommentItemUuids } from '../utils/watchedProjects';

// Import components and hooks
import { TreeViewContext } from '../components/tree/TreeViewContext';
import ContextMenu from '../components/tree/ContextMenu';
import DeleteMultipleDialog from '../components/dialogs/DeleteMultipleDialog';
import AddDialog from '../components/AddDialog';
import UnifiedContainerSelector from '../components/tree/UnifiedContainerSelector';
import useTreeContextMenu from '../hooks/useTreeContextMenu';
import RequirementsTree from '../components/requirements/RequirementsTree';
import { planMoveSibling, getSiblingOrder } from '../components/tree/RequirementsTreeGrid';
import { Ic } from '../components/shell/Icons';
import { Z_MOBILE_ASSISTANT_BACK } from '../utils/zLayers';
import { getPref, setPref } from '../services/prefs';
import { usePref } from '../services/prefs';

import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useTreeState } from '../hooks/useTreeState';
import useReviewActivity from '../hooks/useReviewActivity';
import { useFailedBranchRecovery } from '../hooks/useFailedBranchRecovery';
import useAutoFocus from '../hooks/useAutoFocus';
import { useTreeSync } from '../contexts/TreeSyncContext';
import { useAuth } from '../contexts/AuthContext';
import { itemNeighbours } from '../components/itemDetails/itemSequence';
import { useLocation, useNavigate } from 'react-router';
import useTypeMap from '../hooks/useTypeMap';
import { itemIdFromPath } from '../hooks/itemUrl';

/**
 * RequirementsTreeView component displays a hierarchical view of requirements
 * and supports URL-based navigation, multi-selection, and context menu actions
 */
export default function RequirementsTreeView() {
  // UI state
  const [contextMenu, setContextMenu] = useState(null);
  // In-app confirmation modal (replaces window.confirm, #943). Holds the descriptor
  // for the pending action — { title, message, severity, confirmLabel, confirmColor,
  // testId, onConfirm } — or null when nothing is awaiting confirmation.
  const [confirm, setConfirm] = useState(null);
  const [, setGridTemplateColumns] = useState('35% 8px calc(45% - 16px) 8px 20%');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [chatPanelVisible, setChatPanelVisible] = useState(() => getPref('assistantPanel'));
  const [mobileAiOpen, setMobileAiOpen] = useState(false); // FAB-opened assistant overlay (mobile)
  // #1032: minimizing the assistant (from its own burger menu) must actually
  // unmount the whole `.aicol` column, not just swap ChatInterface's own
  // internal view — leaving the outer column mounted at its sized width was
  // exactly the "empty white column" bug. Mirrors the desktop/mobile split the
  // column render below already has: on mobile close the overlay, on desktop
  // hide the column AND persist the closed state as the pref default going
  // forward (so a deliberate close sticks across reloads, same as the toggle
  // in Settings -> Display already does).
  const handleChatMinimize = useCallback(() => {
    if (mobileAiOpen) { setMobileAiOpen(false); return; }
    setChatPanelVisible(false);
    setPref('assistantPanel', false);
  }, [mobileAiOpen]);

  const [treeVisible, setTreeVisible] = useState(true);
  // When the tree column is hidden, the item page can still summon it as a
  // left slide-over overlay (consistent with the mobile Back/overlay model).
  const [treeOverlay, setTreeOverlay] = useState(false);
  // Tree-local text search (lives in the filter popover). Independent of the
  // top-bar field (which is becoming a command palette).
  const [treeSearch, setTreeSearch] = useState('');
  // Resizable columns (persisted): tree | detail(flex) | assistant.
  const [treeW, setTreeW] = useState(() => Number(localStorage.getItem('rmdv_treeW')) || 312);
  const [aiW, setAiW] = useState(() => Number(localStorage.getItem('rmdv_aiW')) || 300);
  useEffect(() => { try { localStorage.setItem('rmdv_treeW', String(treeW)); } catch { /* ignore */ } }, [treeW]);
  useEffect(() => { try { localStorage.setItem('rmdv_aiW', String(aiW)); } catch { /* ignore */ } }, [aiW]);

  // #150 (command palette): "New Requirement"/"New Container" are global actions
  // (available from any page, via CommandPalette in AppShell), but the actual
  // create flow — project/type resolution, the AddDialog itself — only exists
  // here on the tree. Rather than duplicate that logic in a second, standalone
  // dialog, the palette navigates to '/' and dispatches this event; the same
  // top-level "add" path the toolbar's own background-click uses handles it.
  useEffect(() => {
    const onNewItem = (e) => {
      setAddDialogParentId(null);
      setAddDialogIsContainer(!!e.detail?.isContainer);
      setAddDialogOpen(true);
    };
    window.addEventListener('rmdv:new-item', onNewItem);
    return () => window.removeEventListener('rmdv:new-item', onNewItem);
  }, []);

  // #314: the tree's own "hide" button toggles local `treeVisible` state, which a
  // same-route nav click can't reach (React Router no-ops navigating to the route
  // you're already on, so this component never remounts to reset it). AppShell's
  // bottom-nav tree button and hamburger "Requirements" both broadcast this event
  // when clicked while on '/' — bring the tree back exactly like pressing "hide"
  // a second time would.
  //
  // #373: when an item is open on mobile (its detail is a full-screen overlay),
  // AppShell asks (via `detail.asOverlay`) for the tree to come up as the left
  // slide-over OVERLAY that floats *over* the detail rather than deselecting the
  // item — so dismissing the tree (its Close button or the scrim to its right)
  // returns to the very requirement the user was viewing, instead of dropping them
  // on an empty "select an item" screen.
  useEffect(() => {
    const onShowTree = (e) => {
      if (e?.detail?.asOverlay) {
        setTreeVisible(false);
        setTreeOverlay(true);
      } else {
        setTreeVisible(true);
        setTreeOverlay(false);
      }
    };
    window.addEventListener('rmdv:show-tree', onShowTree);
    return () => window.removeEventListener('rmdv:show-tree', onShowTree);
  }, []);
  // Shared bounds with the drag clamps below — keep both in sync so the
  // keyboard and pointer resize paths can never disagree on range.
  const COL_BOUNDS = { tree: { min: 220, max: 560 }, ai: { min: 240, max: 560 } };
  const startColResize = (which) => (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startTree = treeW;
    const startAi = aiW;
    const onMove = (ev) => {
      if (which === 'tree') setTreeW(Math.min(560, Math.max(220, startTree + (ev.clientX - startX))));
      else setAiW(Math.min(560, Math.max(240, startAi - (ev.clientX - startX))));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  // #793/#149: keyboard equivalent of the drag handle above (APG "Window
  // Splitter"). Deliberately `role="slider"` on the element rather than
  // `role="separator"` — jsx-a11y's role model (aria-query) classifies
  // `separator` as non-interactive regardless of what handlers it carries, so
  // no amount of keyboard support silences the lint warning on that role; a
  // resizable divider's "value" IS a pane width, which is exactly what
  // `slider` expresses, and aria-query correctly treats `slider` as a widget.
  // This also closes the real gap the panes had: they were previously
  // unresizable without a pointer.
  const resizeColByKey = (which) => (e) => {
    const { min, max } = COL_BOUNDS[which];
    const setW = which === 'tree' ? setTreeW : setAiW;
    // 'ai' sits right-of-splitter (mouse-right shrinks it, see onMove above) —
    // mirror that sign convention for the arrow keys.
    const dir = which === 'ai' ? -1 : 1;
    if (e.key === 'ArrowLeft') { e.preventDefault(); setW((w) => Math.max(min, w - dir * 16)); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); setW((w) => Math.min(max, w + dir * 16)); }
    else if (e.key === 'Home') { e.preventDefault(); setW(min); }
    else if (e.key === 'End') { e.preventDefault(); setW(max); }
  };
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogParentId, setAddDialogParentId] = useState(null);
  const [addDialogIsContainer, setAddDialogIsContainer] = useState(false);
  // #408: the item awaiting a "Move…" destination pick (tree-picker modal).
  const [moveTarget, setMoveTarget] = useState(null);
  const [bulkAiTarget, setBulkAiTarget] = useState(null);
  // #275: the tree's "+" is now a dropdown (New Project / New Item / New
  // Container), not a single top-level-requirement shortcut.
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  // #275/#287: one dialog instance drives both "New Project" (tree "+", and a
  // project's own right-click menu) and "Copy Project" (project right-click
  // menu) — null | { mode: 'create' } | { mode: 'copy', pid, name }.
  const [projectDialog, setProjectDialog] = useState(null);
  // #195: "Archive Project…" from the tree's project context menu — null | { id, name }.
  const [archiveProjectTarget, setArchiveProjectTarget] = useState(null);
  // Tree filtering: the tree's own filter-popover search + a per-type filter.
  // Intentionally NOT fed by the topbar GlobalSearchBox (#285) — that box is a
  // jump-to (pick a result → open the item), decoupled from tree filtering.
  // Debounce the query that drives the server-side tree search so it fires once per
  // settled word, not once per keystroke. The tree search fans out to ONE /items/search
  // per visible project (Promise.all in useTreeState), so for a multi-project user each
  // keystroke multiplied: typing "nvm" (3 keystrokes) against ~5 projects fired ~15
  // concurrent requests, which saturated the backend and raced — the tree settled on
  // whichever stale response resolved last ("same query, different results"). Debouncing
  // collapses the keystroke dimension; the seq guard in useTreeState makes it latest-wins.
  // Debounce only NON-empty terms — an empty term commits immediately. With a
  // uniform delay, clear-then-retype of the SAME term inside one 280ms window means
  // the debounced value never changes (term -> pending '' -> term), so no re-query
  // fires and the view keeps the pre-clear result set: stale rows after a mutation
  // (#753 — the cascade-archive spec caught it; a fast human clearing and retyping
  // hits the identical race). Instant clear kills the window at its root.
  const debouncedSearch = useDebouncedValue(treeSearch, treeSearch ? 280 : 0);
  // Per-user project scope (#125): which projects are visible + the group-label prefix.
  // Resolved server-side from membership; the picker lives in Settings → Display.
  const { activeOrg, activeProject } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const routeItemId = itemIdFromPath(location.pathname);
  // #1092: `?type=<type_id>` filters the tree to every item of that type in the current
  // project; `&scope=all` widens it to every visible project (current one opened).
  const urlParams = new URLSearchParams(location.search || '');
  const typeIdFilter = urlParams.get('type') || null;
  const typeScopeAll = urlParams.get('scope') === 'all';
  const typeLabels = useTypeMap();
  // Show the active type filter where the user can read and clear it.
  useEffect(() => { if (typeIdFilter) setTypeFilterOpen(true); }, [typeIdFilter]);
  const clearTypeIdFilter = useCallback(() => {
    const next = new URLSearchParams(location.search || '');
    next.delete('type');
    next.delete('scope');
    const qs = next.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '', hash: location.hash }, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);
  const visibleProjects = usePref('visibleProjects');
  const projectPrefix = usePref('projectPrefix');
  const [typeFilterOpen, setTypeFilterOpen] = useState(false);
  // #793: swaps this jsx-a11y/no-autofocus site for the shared hook, for
  // consistency with every other summoned-popover/dialog input in this app.
  // See hooks/useAutoFocus.
  const typeFilterFocusRef = useAutoFocus(typeFilterOpen);
  // #568/#569: legend popover explaining every tree marker (approval dot colours +
  // the open-comment glyph) — the reported "orange dot, unclear what it means".
  const [legendOpen, setLegendOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState([]); // [] = all; e.g. ['container']
  const toggleTypeFilter = (kind) => setTypeFilter((prev) => (
    prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]
  ));
  // #350/#349: outstanding-review-state filter (each state toggled separately) +
  // "show archived". `showArchived` also drives the lazy loader to fetch archived items.
  const [reviewFilter, setReviewFilter] = useState([]); // e.g. ['archive_proposed','unverified']
  const toggleReviewFilter = (state) => setReviewFilter((prev) => (
    prev.includes(state) ? prev.filter((s) => s !== state) : [...prev, state]
  ));
  const [showArchived, setShowArchived] = useState(false);

  // #342 Phase 2 — set of item_uuids with open review comments in the active
  // project, so the tree can amber-dot them. One lightweight fetch of the
  // (page-capped) project feedback queue; re-fetched when the project changes
  // or the tree reloads (so a just-added/resolved comment updates the dots).
  const [openCommentItems, setOpenCommentItems] = useState(() => new Set());

  // #569: id -> {state, mine} approval rollup for every currently-LOADED item
  // (fetched in bulk below, keyed off `flatRequirements`) — the tree dot's data
  // source (RequirementsTree.jsx no longer reads the status attribute). Merged
  // (not replaced) on each fetch so a still-loading id keeps whatever it had.
  const [approvalStates, setApprovalStates] = useState({});
  // #567: an Approve/Unapprove from the open item-detail view updates just that
  // one id's entry — no tree/network refetch at all (approvals live in
  // `item_approvals`, never the item row, so there's nothing else to reload).
  const handleApprovalChange = useCallback((itemId, data) => {
    setApprovalStates((prev) => ({ ...prev, [itemId]: data }));
  }, []);

  // Use the enhanced tree state hook (LAZY loading + project scope + server search)
  const {
    // Data and state
    items,
    expanded,
    setExpanded,
    selected,
    setSelected,
    loading,
    error,
    snackbar,
    loadingIds,
    failedChildIds,
    retryChildren,
    searchActive,
    myProjects,
    reloadMyProjects,

    // Tree operations
    refreshTree,
    loadChildren,
    loadMore,
    // #567: optimistic single-node update/remove — see useTreeState.js.
    patchNode,
    removeNode,

    // Item operations
    findItemById,
    deleteItem,
    deleteMultipleItems,
    decomposeItem,
    addNewItem,
    moveItem,

    // UI feedback
    showSnackbar,
    handleCloseSnackbar
  } = useTreeState({
    activeOrg, visibleProjects, projectPrefix, searchQuery: debouncedSearch,
    includeArchived: showArchived,
    // #350/#428: while a review-state filter is on, the hook fetches the
    // SERVER-pruned filter tree for these states (matches + ancestor
    // scaffolding) instead of eager-loading whole project trees for a
    // client-side filter.
    reviewFilter,
    // #1092: the type id from the URL, its scope, and the project to keep open.
    typeIdFilter, typeScopeAll, activeProjectId: activeProject,
    // #817: `/item/<id>` renders this page, and an in-app navigation to it uses pushState,
    // which fires no hashchange — so the mounted tree is told by prop, not by event.
    routeItemId,
  });

  // N-21 / REQ-6133 (#609): the live-refresh signal. `changeCount` ticks once per
  // OBSERVED change to the project's review state (another reviewer's approval, or a
  // comment) — see `useReviewActivity` for why this is a polled probe and not SSE.
  //
  // This surface needs it BECAUSE of the #595 fix directly above: the tree used to
  // re-request the whole cumulative id set on every expand, which accidentally
  // re-polled approval state as a side effect. Removing that waste removes the
  // accident too, so the refresh has to become deliberate — the two changes were
  // designed together, and shipping only the first would have made the tree dots MORE
  // stale, not less.
  //
  // Watched set = the caller's view-permitted projects, narrowed to the ones the user
  // has chosen to see. Deliberately `myProjects`, NOT `items` and NOT `activeProject`:
  //   - `activeProject` is a user SELECTION and is routinely null while the tree shows
  //     every visible project grouped. Measured in the running app: keyed on it, the
  //     probe effect fired once with `null` and never polled at all.
  //   - `items` is FILTERED. The moment the tree is searched/filtered its project
  //     grouping collapses, the derived set empties, and polling tears down and never
  //     restarts — i.e. live refresh dies exactly when a reviewer narrows to the item
  //     they are reviewing. (`myProjects` documents itself as the filter-independent
  //     source for precisely this reason.) Both were observed in a real browser, not
  //     reasoned about.
  // #620: pulled into `utils/watchedProjects.js` (`computeWatchedProjectIds`) so
  // the "never keyed on `activeProject`" invariant is directly unit-testable —
  // see that module's header for the bug this guards.
  const watchedProjectIds = useMemo(
    () => computeWatchedProjectIds(myProjects, visibleProjects),
    [myProjects, visibleProjects],
  );
  // #697 acceptance criterion 2 — "no polling from a mounted-but-hidden view".
  //
  // The hook's `document.visibilityState` standdown does not cover this case, and that is
  // exactly why the criterion exists: the TAB is visible, the tree COLUMN is not. Hiding
  // it sets local `treeVisible` state and this component deliberately never remounts
  // (#314), so without an explicit flag the widest watched set in the app — every
  // membership — keeps polling to refresh dots and counts nobody can see.
  //
  // `ItemDetails` is unaffected: it renders inside this view but subscribes separately
  // for its own project, so a reviewer who hides the tree to read an item keeps live
  // refresh on the thing they are actually looking at and loses it only for the tree
  // that is off screen.
  const treeOnScreen = treeVisible || treeOverlay;
  const { changeCount: reviewChangeCount } = useReviewActivity(
    watchedProjectIds, { enabled: treeOnScreen });

  // #342 Phase 2 — the open-comment item set behind the tree's comment dots. Best-effort
  // (empty set on failure). #492: NOT keyed on `items` — the set is by item_uuid and does
  // not change just because the tree data reloaded, so an `items` dep re-fetched this
  // page-capped endpoint on every expand/refresh for no benefit.
  //
  // N-21 / REQ-6133 (#609), two changes:
  //  - keyed on the SAME watched-project set as the approval probe rather than
  //    `activeProject`. `activeProject` is a user SELECTION and is null whenever the tree
  //    shows several projects grouped, so these dots simply never loaded on a
  //    multi-project tree — a pre-existing defect, independent of live refresh, found
  //    while driving the real app. The two dot types render on the same rows, so they
  //    must agree about which projects they describe.
  //  - re-fetched when the probe reports another reviewer changed something: a comment
  //    raised or resolved elsewhere is review state, and used to need a manual reload.
  //  - 2026-07-28, owner's dev smoke test: this was the SECOND review storm. It called
  //    the per-project route once per watched project — `Promise.all(ids.map(...))` —
  //    and re-fired the whole fan-out on every `reviewChangeCount` bump, i.e. every time
  //    any watched project changed. That is exactly the fan-out #697 removed from the
  //    probe, three lines above this one, left untouched because the fix was scoped to
  //    the endpoint that was named rather than to the SHAPE. One batch request now.
  const watchedProjectIdsKey = watchedProjectIds.join(',');
  useEffect(() => {
    let cancelled = false;
    const ids = watchedProjectIdsKey ? watchedProjectIdsKey.split(',') : [];
    if (!ids.length) { setOpenCommentItems(new Set()); return undefined; }
    requirementsApi.getOpenCommentItemsBatch(ids, { status: 'open' })
      .then((res) => {
        if (cancelled) return;
        // #620: extracted to `utils/watchedProjects.js` for direct unit coverage
        // of the multi-project merge (2+ projects' `item_uuids` combining into
        // one Set) — the exact shape this issue's regression test pins.
        setOpenCommentItems(mergeOpenCommentItemUuids(res));
      })
      .catch(() => {
        // DO NOT blank the set on failure.
        //
        // It used to `setOpenCommentItems(new Set())` here, which converts a transport
        // failure into a DOMAIN CLAIM: every comment dot disappears, and the tree tells
        // the reviewer "nothing here needs your attention" on the strength of a request
        // that never arrived. That is the silent-omission class (F-21 / #581) and it is
        // the failure mode with the worst possible direction — a reviewer skips an item
        // precisely because the app could not tell them to look at it.
        //
        // Keeping the previous set is stale at worst, and staleness is visible when
        // acted on; a false "all clear" is not. The next successful fetch corrects it.
        //
        // This effect is not itself a timer: it re-runs only on a watched-set change or
        // a `reviewChangeCount` bump, and that counter only advances when the shared
        // probe SUCCEEDS — and the probe is health-gated. So a dead backend produces no
        // repeat of this request either, without needing its own gate.
      });
    return () => { cancelled = true; };
  }, [watchedProjectIdsKey, reviewChangeCount]);

  // Owner ask, 2026-07-28: "'Failed to load' with a refresh button (or an auto refresh on
  // health recovery (2x?) + delay)". Both, not either — the button is the guaranteed
  // affordance, and this spares the user from needing it in the common case, where the
  // branch failed because the backend was briefly unreachable (a Render cold start, a
  // dropped connection) and is now fine.
  //
  // #750: this used to be inlined here as an EDGE-triggered check (fire only when the
  // health service itself observed a failed→ok transition). That missed any branch
  // failure that fell entirely inside one 15s heartbeat gap — the branch's own fetch
  // failed and recovered without a health ping ever seeing it, so the edge never fired
  // and the branch sat "Failed to load" until a manual Retry. `useFailedBranchRecovery`
  // is the same machinery (same delay, same 2-ping threshold) made STATE-based instead:
  // it checks `failedChildIds` directly on every healthy heartbeat rather than waiting
  // for an observed outage. Full rationale + the retry-storm bound (`RECOVERY_MAX_AUTO_ATTEMPTS`)
  // live in that hook's header comment.
  const { manualRetryChildren } = useFailedBranchRecovery({ failedChildIds, retryChildren });

  // Context for tree view
  const contextValue = useMemo(() => ({
    contextMenu,
    setContextMenu
  }), [contextMenu]);

  // #275 follow-up: on mobile, opening an item shows it full-screen and the only
  // way back to the tree toolbar (where the "+" menu lives) is "Back to list",
  // which clears `selected` to `[]` (see the m-back button below) so the add-menu's
  // "New Item"/"New Container" project/parent resolution — which read `selected`
  // directly — went blank and stayed permanently disabled on mobile. Track the last
  // non-empty single selection separately so it survives that clear and the add
  // menu can still target "wherever I was last looking", per the reported ask.
  const [lastSelectedItemId, setLastSelectedItemId] = useState(null);
  useEffect(() => {
    if (selected.length === 1) setLastSelectedItemId(selected[0]);
  }, [selected]);

  // Tree-sync: when the chat agent reports a requirement was modified, the
  // shared TreeSyncContext bumps refreshTrigger — refresh the tree in response.
  const { refreshTrigger } = useTreeSync();
  // `refreshTree` is NOT stable — the comment previously here claimed it was. useTreeState
  // rebuilds it whenever `expanded` changes, i.e. every time the user opens a branch — so
  // it cannot go in the dependency array: a full refresh (top level + every open branch
  // re-fetched) would fire on every expand. Read the latest one through a ref instead, so
  // the tick stays the only trigger while the callback invoked is always the current one.
  const refreshTreeRef = useRef(refreshTree);
  useEffect(() => { refreshTreeRef.current = refreshTree; }, [refreshTree]);
  useEffect(() => {
    if (refreshTrigger > 0) {
      refreshTreeRef.current();
    }
  }, [refreshTrigger]);

  // #440: the home "no item selected" view now shows the rationalised analytics
  // dashboard (DashboardHome), which reads ONE bounded server aggregate
  // (/items/summary) instead of the retired CoveragePanel/QualityPanel/ActivityFeed
  // (each of which fetched the slow /coverage ~6-10s, /quality, /activity on mount and
  // competed with the tree's own /items load — #433/#215). That whole class of
  // critical-path contention is gone: `/` no longer calls /coverage or /quality at all,
  // so the #433 defer latch it needed (analyticsEnabled) is retired with the panels.
  // On mobile the dashboard is NOT mounted (no fetch) — the small screen shows the tree.
  const isMobile = useMediaQuery('(max-width:640px)');

  // Flatten the hierarchical items into a flat list so requirement-link
  // existence checks (RequirementLink) can resolve any ID, not just roots.
  const flatRequirements = useMemo(() => {
    const out = [];
    const walk = (nodes) => {
      (nodes || []).forEach(n => {
        if (!n) return;
        out.push(n);
        if (Array.isArray(n.children) && n.children.length) walk(n.children);
      });
    };
    walk(items);
    return out;
  }, [items]);

  // #569: bulk-fetch the approval-state rollup for every currently-loaded item id
  // whenever the loaded set changes (expand/page/project switch/refresh) — a
  // request keyed on a STABLE joined-ids string (not the `flatRequirements` array
  // reference) so it doesn't refire on unrelated re-renders. Deliberately separate
  // from the perf-critical `GET /api/items` tree payload (spec #569): this is one
  // extra bulk call, not a per-row fetch, and never blocks tree rendering — best
  // effort, dots simply stay absent on failure.
  //
  // N-20 / REQ-6132 (#595): the request now carries only the DELTA — the ids not
  // already asked for — instead of the whole cumulative loaded set on every expand.
  // Before, each expand re-sent every id ever loaded, so the request grew
  // monotonically while returning data the client already had; past the server's
  // per-request cap that turned into dropped rows (and, past the transport's URL
  // ceiling, an opaque 400 that cost the WHOLE tree its dots).
  //
  // The ledger tracks ids REQUESTED, not ids returned. An id the caller cannot see
  // (cross-tenant, deleted) is legitimately absent from the response — keying off
  // "missing from the map" would re-request it on every render, forever.
  //
  // The ledger (and the cached states it gates) is dropped on a project switch —
  // the SAME invalidation key the open-comment set above uses. Without this, the
  // delta would make the map permanently sticky across projects; with it, the
  // stickiness is bounded to one project's session, which is the window the live
  // refresh (N-21 / REQ-6133, #609) covers on the Breakdown.
  const requestedApprovalIdsRef = useRef(null);
  if (requestedApprovalIdsRef.current === null) requestedApprovalIdsRef.current = new Set();
  useEffect(() => {
    requestedApprovalIdsRef.current = new Set();
    setApprovalStates({});
  }, [activeProject]);
  const loadedItemIdsKey = useMemo(
    () => flatRequirements.map((n) => n.id).filter(Boolean).join(','),
    [flatRequirements],
  );
  //
  // N-21 / REQ-6133 (#609): when the probe reports another reviewer changed something,
  // the ledger is CLEARED so the next pass re-asks for every loaded id. The delta
  // optimisation and the live refresh are the same mechanism read two ways — the
  // ledger says "I already know this", and a change marker is precisely the statement
  // that what I know is no longer true.
  const lastReviewChangeRef = useRef(0);
  useEffect(() => {
    let cancelled = false;
    if (!loadedItemIdsKey) return undefined;
    const ledger = requestedApprovalIdsRef.current;
    if (reviewChangeCount !== lastReviewChangeRef.current) {
      lastReviewChangeRef.current = reviewChangeCount;
      ledger.clear();
    }
    const pending = loadedItemIdsKey.split(',').filter((id) => id && !ledger.has(id));
    if (!pending.length) return undefined;
    pending.forEach((id) => ledger.add(id));
    requirementsApi.getApprovalStates(pending)
      .then((res) => { if (!cancelled) setApprovalStates((prev) => ({ ...prev, ...res })); })
      .catch(() => {
        // Best-effort — dots simply stay absent. Un-claim the ids so a transient
        // failure is retried on the next tree change rather than being remembered
        // as "already fetched" and never asked for again.
        pending.forEach((id) => ledger.delete(id));
      });
    return () => { cancelled = true; };
  }, [loadedItemIdsKey, reviewChangeCount]);

  // Which project's item types the Add dialog's type selector should offer (#197).
  // There's no explicit project-switcher in this view (the sidebar one is
  // orphaned/unreachable — see AddDialog.js's own comment), so resolve the best
  // available signal: the ADD's parent item's own project (adding a child inherits
  // its parent's project, same rule the backend's resolve_target_project uses) —
  // else, for a top-level add, the org's single project when it has exactly one.
  // Deliberately sourced from `myProjects` (the caller's view-permitted projects,
  // independent of the tree's current search/filter), NOT from the currently
  // rendered `items` — those can be legitimately filtered down to ZERO rows (e.g.
  // the background-menu Add flow filters to a guaranteed no-match first so its
  // right-click lands on empty space), which would wrongly read as "no project in
  // scope" even for an unambiguous single-project org. An org with NO projects at
  // all (legacy/unscoped) or 2+ projects yields no projectId here, same as today.
  // #287: set when "New Item"/"New Container" is invoked from a PROJECT's own
  // right-click menu — an explicit, unambiguous target that must win over the
  // parent-inference/single-project fallback below (the whole point of the
  // project menu is picking a specific project when several are visible).
  const [addDialogProjectIdOverride, setAddDialogProjectIdOverride] = useState(null);
  const addDialogProjectId = useMemo(() => {
    if (addDialogProjectIdOverride) return addDialogProjectIdOverride;
    if (addDialogParentId) {
      const parent = flatRequirements.find((n) => n && n.id === addDialogParentId);
      const pid = parent?.projectId || parent?.project_id;
      if (pid) return pid;
    }
    return (myProjects && myProjects.length === 1) ? myProjects[0].id : null;
  }, [addDialogProjectIdOverride, addDialogParentId, flatRequirements, myProjects]);

  // #275: "New Item"/"New Container" in the tree's "+" menu need an unambiguous
  // project to land in — same resolution `addDialogProjectId` uses (a selected
  // item's own project, else the org's single project). Greyed out + tooltipped
  // otherwise (e.g. nothing selected in a multi-project org), rather than
  // silently guessing which project to create in. Falls back to `lastSelectedItemId`
  // (the last item selected OR opened) when nothing is currently selected — see
  // its declaration above for why (mobile's "Back to list" clears `selected`).
  const targetItemId = selected.length === 1 ? selected[0] : lastSelectedItemId;
  const singleSelectedItem = targetItemId
    ? flatRequirements.find((n) => n && n.id === targetItemId)
    : null;
  const canAddItemOrContainer = Boolean(singleSelectedItem)
    || Boolean(myProjects && myProjects.length === 1);
  const addDisabledReason = singleSelectedItem || (myProjects && myProjects.length === 1)
    ? undefined
    : 'Select a project or an item/container first';

  // #275 follow-up: "the new item modal should clearly indicate the path which
  // the new [item] shall be placed into" — walk the hierarchical `items` tree
  // from root down to the resolved parent (or to the resolved project's name,
  // for a top-level add) and join into a breadcrumb string for AddDialog.
  const findAncestorTitles = useCallback((nodes, id, trail = []) => {
    for (const n of (nodes || [])) {
      if (!n) continue;
      const nextTrail = [...trail, n.title || n.display_id || n.id];
      if (n.id === id) return nextTrail;
      if (Array.isArray(n.children) && n.children.length) {
        const found = findAncestorTitles(n.children, id, nextTrail);
        if (found) return found;
      }
    }
    return null;
  }, []);
  // #408: the chain of node IDS from root down to `id` (inclusive) — so the "Move…"
  // picker can pre-expand to the item's current location.
  const findAncestorIds = useCallback((nodes, id, trail = []) => {
    for (const n of (nodes || [])) {
      if (!n) continue;
      const nextTrail = [...trail, n.id];
      if (n.id === id) return nextTrail;
      if (Array.isArray(n.children) && n.children.length) {
        const found = findAncestorIds(n.children, id, nextTrail);
        if (found) return found;
      }
    }
    return null;
  }, []);
  const addDialogParentPath = useMemo(() => {
    if (addDialogParentId) {
      const trail = findAncestorTitles(items, addDialogParentId);
      if (trail) return trail.join(' › ');
    }
    const projectName = (myProjects || []).find((p) => p.id === addDialogProjectId)?.name;
    return projectName ? `${projectName} (top level)` : null;
  }, [addDialogParentId, items, findAncestorTitles, myProjects, addDialogProjectId]);

  // Project scope + multi-project grouping are resolved inside useTreeState now (#125):
  // `items` is already the correct top level — scoped roots for a single project, or
  // top-level project group nodes (prefix + name) when several projects are visible.
  const displayedItems = items;
  // #1092: under `scope=all` other projects stay closed; the current project opens.
  const eagerCollapsedIds = useMemo(() => (
    typeIdFilter && typeScopeAll
      ? (items || []).filter((n) => n._kind === 'project' && n.projectId !== activeProject).map((n) => n.id)
      : []
  ), [items, typeIdFilter, typeScopeAll, activeProject]);

  // Chat context for the currently-selected items — a pure function of `selected`
  // + `items` (no fetch, no subscription, no other setter anywhere in this file),
  // so it is computed during render rather than mirrored into state via an effect
  // (react-hooks/set-state-in-effect, #943). Behaviour-preserving: same shape on
  // both branches as the effect it replaces, recomputed on the same two deps.
  const chatContext = useMemo(() => (
    selected.length > 0
      ? {
        current_view: window.location.pathname,
        selected_items: selected.map(id => findItemById(items, id)).filter(Boolean),
        system_info: { timestamp: new Date().toISOString(), view: 'requirements-tree' },
      }
      : {
        current_view: window.location.pathname,
        selected_items: [],
        system_info: { timestamp: new Date().toISOString(), view: 'requirements-tree' },
      }
  ), [selected, items, findItemById]);

  // Context menu handling
  const { handleContextMenu } = useTreeContextMenu(
    items,
    findItemById,
    selected,
    setContextMenu
  );

  const handleContextMenuClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  /**
   * Handle context menu actions for tree items
   */
  const handleContextMenuAction = useCallback((action, itemId, item, selectedIds) => {
    console.log('Context menu action:', { action, itemId, item, selectedIds });
    
    // Handle background context menu actions (itemId is null or undefined)
    const isBackgroundAction = itemId === null || itemId === undefined;
    
    // Only check for missing itemId if it's not a background action
    if (!isBackgroundAction && !itemId) {
      console.error('No item ID provided for action:', action);
      return;
    }
    
    // Get all selected items if the clicked item is part of the selection
    const targetIds = isBackgroundAction 
      ? [] 
      : (selectedIds || (selected.includes(itemId) ? [...selected] : [itemId]));
    const targetItems = targetIds.map(id => findItemById(items, id)).filter(Boolean);

    // #735: New Project is org-level — no item/project target needed — and is now
    // ALSO the background menu's only entry, so handle it before any anchor-kind
    // branching (the background anchor carries no item and would fall past the
    // project-node branch below).
    if (action === 'newProject') {
      setProjectDialog({ mode: 'create' });
      return;
    }

    // #287: a right-click on a PROJECT group node — its own menu (New Project /
    // Copy Project / New Item / New Container / Export to Markdown), handled
    // entirely separately from the regular item/background actions below.
    if (item?._kind === 'project') {
      switch (action) {
        case 'newProject':
          setProjectDialog({ mode: 'create' });
          return;
        case 'copyProject':
          setProjectDialog({ mode: 'copy', pid: item.projectId, name: item.title });
          return;
        case 'add':
        case 'addContainer':
          // Top-level within THIS project specifically — not wherever the
          // parent-inference/single-project fallback would otherwise land.
          setAddDialogParentId(null);
          setAddDialogProjectIdOverride(item.projectId);
          setAddDialogIsContainer(action === 'addContainer');
          setAddDialogOpen(true);
          return;
        case 'exportProjectMarkdown':
          // #382: give explicit progress + success feedback — a successful export
          // previously showed NOTHING (no toast, only failures spoke), so a slow
          // but working export read as "the button does nothing".
          showSnackbar('Preparing Markdown export…', 'info');
          iamApi.exportProjectMarkdown(item.projectId)
            .then((data) => {
              downloadTextFile(`${item.title}.md`, data?.content || '');
              showSnackbar('Markdown export downloaded', 'success');
            })
            .catch((err) => showSnackbar(`Export failed: ${err.message}`, 'error'));
          return;
        case 'archiveProject':
          // #195: the raw project name (for the "type to confirm" dialog) —
          // `item.title` is the tree's PREFIXED display label (projectPrefix +
          // name), not the real name `ProjectDangerDialog` needs the user to type.
          setArchiveProjectTarget({
            id: item.projectId,
            name: myProjects.find((p) => p.id === item.projectId)?.name || item.title,
          });
          return;
        default:
          return;
      }
    }

    switch (action) {
      case 'add':
        if (isBackgroundAction) {
          console.log('Add top-level requirement');
          setAddDialogParentId(null);
          setAddDialogIsContainer(false);
          setAddDialogOpen(true);
        } else {
          console.log('Add item under parent:', itemId);
          setAddDialogParentId(itemId);
          setAddDialogIsContainer(false);
          setAddDialogOpen(true);
        }
        break;
      case 'addContainer': {
        if (isBackgroundAction) {
          // Empty-space "create container" makes a TOP-LEVEL container (T1.6).
          console.log('Add top-level container');
          setAddDialogParentId(null);
          setAddDialogIsContainer(true);
          setAddDialogOpen(true);
          break;
        }
        console.log('Add container at same level as:', itemId);
        // Find the parent of the current item to add container at same level
        const currentItem = findItemById(items, itemId);
        if (currentItem) {
          // Find parent ID by looking through the tree structure
          const findParentId = (treeItems, targetId, parentId = null) => {
            for (const node of treeItems) {
              if (node.id === targetId) return parentId;
              if (node.children) {
                const found = findParentId(node.children, targetId, node.id);
                if (found !== null) return found;
              }
            }
            return null;
          };
          
          const parentId = findParentId(items, itemId);
          // Open AddDialog for container at the same level (under the same parent)
          setAddDialogParentId(parentId);
          setAddDialogIsContainer(true);
          setAddDialogOpen(true);
        }
        break;
      }
      case 'edit':
        // If only one item is selected or context menu was triggered on a non-selected item
        if (targetIds.length === 1) {
          console.log('Edit item:', itemId);
          setSelected([itemId]); // Update selection to just this item
        } else {
          showSnackbar('Cannot edit multiple items at once. Please select a single item.', 'warning');
        }
        break;
      case 'move':
        // #408: open a tree-picker modal to choose a new parent for this item.
        if (!isBackgroundAction && itemId && targetIds.length <= 1) {
          setMoveTarget({ id: itemId, item });
        } else if (targetIds.length > 1) {
          showSnackbar('Select a single item to move.', 'warning');
        }
        break;
      case 'moveUp':
      case 'moveDown': {
        // SC 2.5.7 Dragging Movements, AA (#648): non-drag reorder — writes
        // through the same sibling_order path drag-reorder uses (planMoveSibling
        // mirrors planDrop's same-parent branch in RequirementsTreeGrid.js).
        if (isBackgroundAction || targetIds.length !== 1) {
          showSnackbar('Select a single item to move.', 'warning');
          break;
        }
        const plan = planMoveSibling(items, itemId, action === 'moveUp' ? 'up' : 'down');
        if (plan) moveItem(plan.id, plan.payload);
        break;
      }
      case 'delete':
        console.log('Delete items:', targetIds);
        if (targetItems.length > 0) {
          if (targetItems.length === 1) {
            // For single item delete, dialog is handled by ContextMenu component
            // Just execute the delete
            deleteItem(targetIds[0]);
          } else {
            // For multiple items, show the multi-delete dialog
            setItemsToDelete(targetItems);
            setDeleteDialogOpen(true);
          }
        }
        break;
      case 'decompose':
        if (targetIds.length === 1) {
          console.log('Decompose item:', itemId);
          decomposeItem(itemId);
        } else {
          showSnackbar('Cannot decompose multiple items at once. Please select a single item.', 'warning');
        }
        break;
      case 'chat':
        // Ensure chat panel is visible
        if (!chatPanelVisible) {
          setChatPanelVisible(true);
          setGridTemplateColumns('35% 8px calc(45% - 16px) 8px 20%');
        }
        break;
      case 'bulkAi':
        if (!isBackgroundAction && itemId) {
          setBulkAiTarget({ id: itemId, title: item?.title || '' });
        }
        break;
      case 'exportMarkdown':
        if (!isBackgroundAction && itemId) {
          requirementsApi.exportMarkdown(itemId)
            .then((data) => {
              downloadTextFile(`${itemId}.md`, data.content);
            })
            .catch((err) => showSnackbar(`Export failed: ${err.message}`, 'error'));
        }
        break;
      default:
        break;
    }
    
    handleContextMenuClose();
  }, [
    handleContextMenuClose,
    selected,
    items,
    findItemById,
    setSelected,
    deleteItem,
    decomposeItem,
    showSnackbar,
    chatPanelVisible,
    myProjects,
    moveItem
  ]);

  /**
   * Handle confirmation for multiple item deletion
   */
  const handleDeleteMultipleItems = useCallback(() => {
    if (itemsToDelete.length === 0) return;
    
    // Extract IDs from the item objects
    const itemIds = itemsToDelete.map(item => item.id);
    
    // Use the deleteMultipleItems function from useTreeState
    deleteMultipleItems(itemIds);
    setItemsToDelete([]);
    setDeleteDialogOpen(false);
  }, [itemsToDelete, deleteMultipleItems]);

  /**
   * Handle AddDialog close
   */
  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setAddDialogParentId(null);
    setAddDialogIsContainer(false);
    setAddDialogProjectIdOverride(null);
  };

  /**
   * Handle AddDialog submission. Passes the FULL item AddDialog built (#197 fix —
   * previously only parent_id/title/description were forwarded, silently dropping
   * `attributes.type`/`type`/`type_id`, so a container OR a custom-typed item
   * created from this tree-level dialog always landed as a plain requirement).
   */
  const handleAddDialogSubmit = async (newItem) => {
    try {
      await addNewItem(newItem);
      handleCloseAddDialog();
    } catch (err) {
      console.error('Failed to add item:', err);
    }
  };

  // Handle tree view events - with unsaved changes check
  const handleSelectedChange = useCallback((event, nodeIds) => {
    // Get the new selection that's different from the current selection
    const newSelection = nodeIds.find(id => !selected.includes(id));
    const currentSelection = selected.length === 1 ? selected[0] : null;

    // Proceed with selection change. Shared by the unguarded path and by the
    // confirmation modal's continuation, so both apply the same effect.
    const applySelection = () => {
      setSelected(nodeIds);
      // Picking an item from the slide-over tree closes the overlay (mirrors the
      // mobile Back model — the tree gets out of the way once you've chosen).
      setTreeOverlay(false);
    };

    // If we're changing from one item to another (not initial selection or deselection)
    if (currentSelection && newSelection) {
      // Find the ItemDetails component through DOM
      const itemDetailsElement = document.querySelector('[data-has-unsaved-changes]');
      if (itemDetailsElement && itemDetailsElement.getAttribute('data-has-unsaved-changes') === 'true') {
        // #943: unsaved changes — ask through the in-app modal instead of a native
        // window.confirm. The selection is left exactly as it was until the modal is
        // confirmed, so dismissing it keeps the current item, matching what cancelling
        // the native prompt did.
        setConfirm({
          title: 'Unsaved changes',
          message: 'You have unsaved changes. Are you sure you want to navigate away?',
          severity: 'warning',
          confirmLabel: 'Navigate away',
          testId: 'tree-unsaved-changes-confirm',
          onConfirm: applySelection,
        });
        return;
      }
    }

    applySelection();
  }, [selected, setSelected]);

  const handleExpandedChange = useCallback((event, nodeIds) => {
    setExpanded(nodeIds);
  }, [setExpanded]);

  const handleItemClick = useCallback(() => {
    // Additional click handling can be added here if needed
  }, []);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((event) => {
    // Never hijack Delete while a dialog/modal is open or the user is typing in
    // a field — otherwise pressing Delete (to confirm another action or to edit
    // text) would trigger tree deletion. (T1.7 — data-loss risk.)
    if (shouldSuppressTreeShortcut()) return;

    // Only handle Delete key and only if items are selected
    if (event.key === 'Delete' && selected.length > 0) {
      // Get selected item objects
      const targetItems = selected.map(id => findItemById(items, id)).filter(Boolean);
      
      if (targetItems.length > 0) {
        event.preventDefault();
        setItemsToDelete(targetItems);
        setDeleteDialogOpen(true);
      }
    }
  }, [selected, items, findItemById]);

  // #793 H: the Delete shortcut used to hang off the `.cols` layout div via
  // `tabIndex={0}` + `onKeyDown`. That made the whole three-column layout a TAB STOP
  // which announced nothing and did nothing once focused — a mystery stop for keyboard
  // and screen-reader users — and it silently made the shortcut depend on focus sitting
  // inside that subtree. Promoted to the window listener this app already uses for its
  // other global shortcuts (AppShell's ⌘K / `/` / `?`), behind the SAME
  // `shouldSuppressTreeShortcut()` guard, so the behaviour is unchanged except that it
  // no longer needs a phantom tab stop to work.
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // #793 H / #149: Escape closes the tree slide-over. It was dismissible by POINTER
  // only (its scrim, or the Close button) — which is why that scrim was a
  // `<div onClick>` accessibility error. Marking the scrim decorative (`aria-hidden`,
  // below) is only honest once the keyboard has its own way out.
  useEffect(() => {
    if (!treeOverlay) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setTreeOverlay(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [treeOverlay]);

  // Loading/error are rendered INSIDE the tree column (below), never as a
  // whole-view replacement: an early-return spinner here unmounted the entire
  // layout — including ChatInterface — on every top-level tree load, so the
  // assistant panel remounted (wiping any typed input and re-fetching
  // /api/chat/models) once per page load and on any refresh that started from
  // an empty tree. That remount is what made chat.spec.ts retry-dependent
  // (#230 / ISSUES_LOG AD1). The sibling columns own their data lifecycles and
  // must stay mounted while the tree loads.
  const treeLoading = loading && items.length === 0;

  // Determine which item to show details for (only when one item is selected)
  const detailsItemId = selected.length === 1 ? selected[0] : null;

  // #817: the tree publishes the ids it is actually SHOWING, in render order, and the
  // Prev/Next pair is derived from that. Held here rather than in ItemDetails because
  // this component already owns both the tree and the overlay, and already owns the
  // navigation action (`setSelected`).
  const [visibleOrder, setVisibleOrder] = React.useState([]);
  const itemNav = React.useMemo(
    () => itemNeighbours(visibleOrder, detailsItemId),
    [visibleOrder, detailsItemId],
  );

  return (
    <TreeViewContext.Provider value={contextValue}>
      {/* #793 H: `tabIndex`/`onKeyDown` moved off this layout div — see the window
          listener above. It is a pure flex container again. */}
      <div className="cols">
        {/* TREE COLUMN (mockup .treecol). Shown as a normal column when
            treeVisible; when the column is hidden it can still be summoned from
            the item page as a left slide-over overlay (treeOverlay). */}
        {treeOverlay && !treeVisible && (
          /* #793 H: decorative click-catcher; Escape closes (effect above). */
          <div className="tree-scrim" onClick={() => setTreeOverlay(false)} aria-hidden="true" />
        )}
        {(treeVisible || treeOverlay) ? (
          <div
            className={`treecol${treeOverlay && !treeVisible ? ' tree-overlay' : ''}`}
            style={treeVisible && !treeOverlay ? { flex: `0 0 ${treeW}px`, width: treeW } : undefined}
          >
            <div className="panel-head">
              {treeOverlay && !treeVisible ? (
                // Overlay mode: offer re-pin (dock back to a column) and close.
                <>
                  <button type="button" className="icon-btn" title="Pin tree (dock as column)"
                    aria-label="Pin tree"
                    onClick={() => { setTreeVisible(true); setTreeOverlay(false); }}>
                    <Ic name="panelr" />
                  </button>
                  <button type="button" className="ptoggle" title="Close" aria-label="Close tree"
                    onClick={() => setTreeOverlay(false)}>
                    <Ic name="panel" />
                  </button>
                </>
              ) : (
                <button type="button" className="ptoggle" title="Hide tree" aria-label="Hide tree"
                  onClick={() => { setTreeVisible(false); setTreeOverlay(false); }}>
                  <Ic name="panel" />
                </button>
              )}
              <span className="t-sec">Requirements</span>
              <div style={{ flex: 1 }} />
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <button type="button"
                  className="icon-btn"
                  title="What do the markers mean?"
                  aria-label="Marker legend"
                  data-testid="tree-legend-btn"
                  onClick={() => setLegendOpen((o) => !o)}
                >
                  <Ic name="help" />
                </button>
                {/* #568/#569: the tree dot used to be ambiguous (two amber-capable
                    dots, no legend — the reported "orange dot, unclear what it
                    means"). This spells out every marker plainly. */}
                {legendOpen && (
                  <div
                    data-testid="tree-legend-popover"
                    className="cardp"
                    style={{ position: 'absolute', top: 30, right: 0, zIndex: 30, minWidth: 230, padding: 10 }}
                  >
                    <div className="t-over" style={{ marginBottom: 6 }}>Tree markers</div>
                    {/* F-23 / REQ-6109 (#607, WCAG 1.4.1): the legend itself must not
                        rely on colour alone to explain what colour means — each swatch
                        below carries the SAME glyph the tree dot renders. */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12.5 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="dot success" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 10, height: 10, fontSize: 7, fontWeight: 900, color: '#fff' }}>✓</span> Approved
                      </span>
                      {/* F-09 / REQ-6099 (#603, D-08): a sign-off that superseded another
                          reviewer's live rejection is a distinct state on the tree — the
                          legend has to name it, or the accent dot is unexplained. */}
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="dot accent" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 10, height: 10, fontSize: 7, fontWeight: 900, color: '#fff' }}>*</span> Approved over another reviewer’s objection
                      </span>
                      {/* F-11 / REQ-6101 (#603): staleness is symmetric — a REJECTION goes
                          stale too, so this entry may not say "Approved · ...". */}
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="dot warning" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 10, height: 10, fontSize: 7, fontWeight: 900, color: '#fff' }}>!</span> Decision stale — content changed since it was recorded
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="dot error" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 10, height: 10, fontSize: 7, fontWeight: 900, color: '#fff' }}>✕</span> Not approved
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="dot muted" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 10, height: 10, fontSize: 7, fontWeight: 900, color: '#fff' }}>○</span> Pending / no approval recorded
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Ic name="comment" sm style={{ color: 'var(--warning)' }} /> Has open review comments
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <button type="button"
                className="icon-btn"
                title="Refresh tree"
                aria-label="Refresh tree"
                onClick={() => refreshTree()}
              >
                <Ic name="refresh" />
              </button>
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <button type="button"
                  className="icon-btn"
                  title="Filter"
                  data-testid="filter-btn-type"
                  aria-label="Filter by type"
                  onClick={() => setTypeFilterOpen((o) => !o)}
                >
                  <Ic name="filter" />
                </button>
                {typeFilterOpen && (
                  <div
                    data-testid="filter-popover-type"
                    className="cardp"
                    style={{ position: 'absolute', top: 30, right: 0, zIndex: 30, minWidth: 220, padding: 10 }}
                  >
                    <div className="t-over" style={{ marginBottom: 6 }}>Search</div>
                    <div className="search" style={{ marginBottom: 10 }}>
                      <Ic name="search" sm />
                      <input
                        data-testid="tree-search"
                        placeholder="Search this project…"
                        value={treeSearch}
                        onChange={(e) => setTreeSearch(e.target.value)}
                        ref={typeFilterFocusRef}
                      />
                      {treeSearch && (
                        <button type="button"
                          className="btn ghost sm"
                          aria-label="Clear search"
                          style={{ padding: '0 4px' }}
                          onClick={() => setTreeSearch('')}
                        >✕</button>
                      )}
                    </div>
                    <div className="t-over" style={{ marginBottom: 6 }}>Filter by type</div>
                    {typeIdFilter && (
                      <div
                        data-testid="filter-type-id-active"
                        style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '3px 0' }}
                      >
                        <span>Type: {typeLabels[typeIdFilter] || typeIdFilter}{typeScopeAll ? ' (all projects)' : ''}</span>
                        <button type="button"
                          className="btn ghost sm"
                          data-testid="filter-type-id-clear"
                          aria-label="Clear type filter"
                          style={{ padding: '0 4px' }}
                          onClick={clearTypeIdFilter}
                        >✕</button>
                      </div>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '3px 0' }}>
                      <input
                        type="checkbox"
                        data-testid="filter-type-container"
                        checked={typeFilter.includes('container')}
                        onChange={() => toggleTypeFilter('container')}
                      />
                      Containers
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '3px 0' }}>
                      <input
                        type="checkbox"
                        data-testid="filter-type-requirement"
                        checked={typeFilter.includes('requirement')}
                        onChange={() => toggleTypeFilter('requirement')}
                      />
                      Requirements
                    </label>
                    <div className="t-over" style={{ margin: '10px 0 6px' }}>Needs review</div>
                    {/* F-21/F-22 / REQ-6107/REQ-6108 (#581): RESOLVED (INT-5). The wire
                        key is now 'unapproved' — the retired 'unverified' name is still
                        ACCEPTED by the backend as an alias, but both it and the tree dot
                        now derive from the `item_approvals` rollup rather than the free-
                        text status attribute, so the server-side eager prefetch and the
                        client-side match finally agree. 'not_approved' (actively
                        rejected) stays a separate state per the owner's resolution:
                        "never reviewed" and "rejected" must remain distinguishable. */}
                    {[
                      ['archive_proposed', 'Proposed for archive'],
                      ['needs_review', 'Open review comments'],
                      ['unapproved', 'Not yet reviewed'],
                      ['not_approved', 'Rejected'],
                    ].map(([key, label]) => (
                      <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '3px 0' }}>
                        <input
                          type="checkbox"
                          data-testid={`filter-review-${key}`}
                          checked={reviewFilter.includes(key)}
                          onChange={() => toggleReviewFilter(key)}
                        />
                        {label}
                      </label>
                    ))}
                    <div className="t-over" style={{ margin: '10px 0 6px' }}>Archived</div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '3px 0' }}>
                      <input
                        type="checkbox"
                        data-testid="filter-show-archived"
                        checked={showArchived}
                        onChange={() => setShowArchived((v) => !v)}
                      />
                      Show archived items
                    </label>
                    <button type="button"
                      className="btn ghost sm"
                      data-testid="filter-clear-type"
                      style={{ marginTop: 6 }}
                      onClick={() => { setTypeFilter([]); setReviewFilter([]); setShowArchived(false); }}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <button type="button"
                  className="icon-btn"
                  title="New…"
                  data-testid="tree-add-btn"
                  aria-label="Create a new project, item, or container"
                  onClick={() => setAddMenuOpen((o) => !o)}
                >
                  <Ic name="plus" />
                </button>
                {addMenuOpen && (
                  <div
                    data-testid="tree-add-menu"
                    className="cardp"
                    style={{ position: 'absolute', top: 30, right: 0, zIndex: 30, minWidth: 190, padding: 4 }}
                  >
                    <button type="button"
                      className="btn ghost sm"
                      data-testid="tree-add-new-project"
                      style={{ width: '100%', justifyContent: 'flex-start', padding: '6px 8px' }}
                      onClick={() => {
                        setAddMenuOpen(false);
                        setProjectDialog({ mode: 'create' });
                      }}
                    >
                      New Project
                    </button>
                    <button type="button"
                      className="btn ghost sm"
                      data-testid="tree-add-new-item"
                      title={canAddItemOrContainer ? undefined : addDisabledReason}
                      aria-disabled={!canAddItemOrContainer}
                      style={{
                        width: '100%',
                        justifyContent: 'flex-start',
                        padding: '6px 8px',
                        opacity: canAddItemOrContainer ? 1 : 0.45,
                        cursor: canAddItemOrContainer ? 'pointer' : 'not-allowed',
                      }}
                      onClick={() => {
                        if (!canAddItemOrContainer) return;
                        setAddMenuOpen(false);
                        setAddDialogParentId(singleSelectedItem ? singleSelectedItem.id : null);
                        setAddDialogIsContainer(false);
                        setAddDialogOpen(true);
                      }}
                    >
                      New Item
                    </button>
                    <button type="button"
                      className="btn ghost sm"
                      data-testid="tree-add-new-container"
                      title={canAddItemOrContainer ? undefined : addDisabledReason}
                      aria-disabled={!canAddItemOrContainer}
                      style={{
                        width: '100%',
                        justifyContent: 'flex-start',
                        padding: '6px 8px',
                        opacity: canAddItemOrContainer ? 1 : 0.45,
                        cursor: canAddItemOrContainer ? 'pointer' : 'not-allowed',
                      }}
                      onClick={() => {
                        if (!canAddItemOrContainer) return;
                        setAddMenuOpen(false);
                        setAddDialogParentId(singleSelectedItem ? singleSelectedItem.id : null);
                        setAddDialogIsContainer(true);
                        setAddDialogOpen(true);
                      }}
                    >
                      New Container
                    </button>
                  </div>
                )}
              </div>
            </div>
            {/* In-column error only when there's no tree to show (initial/refresh
                load failure). CRUD failures also set `error`, but they already
                surface via the snackbar — showing both would double-report (and
                trip strict-mode getByText in the e2e specs). */}
            {error && items.length === 0 && (
              <Box sx={{ p: 1.5 }}>
                <Typography sx={{ color: 'error' }} variant="body2">Error: {error}</Typography>
              </Box>
            )}
            {treeLoading && !error ? (
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
            <RequirementsTree
              // #817: publishes the flattened, filter-applied, expansion-aware id order
              // so the detail overlay's Prev/Next can follow what is on screen.
              onVisibleOrderChange={setVisibleOrder}
              items={displayedItems}
              selected={selected}
              expanded={expanded}
              openCommentItems={openCommentItems}
              approvalStates={approvalStates}
              // Text search is served by the backend (spans the whole project under lazy
              // loading); the tree only applies the client-side type filter on top.
              filterText=""
              typeFilter={typeFilter}
              // #350/#349: when review states are selected AND archived is shown, keep
              // archived items visible alongside the selected states (rather than hidden
              // by the review filter); with no review states, archived just render inline.
              reviewFilter={(showArchived && reviewFilter.length > 0) ? [...reviewFilter, 'archived'] : reviewFilter}
              // #350: when a review filter is on the tree is eager-loaded — tell the
              // tree so it auto-expands every branch and surfaces matches at any depth.
              eager={reviewFilter.length > 0 || Boolean(typeIdFilter)}
              typeIdFilter={typeIdFilter}
              eagerCollapsedIds={eagerCollapsedIds}
              searchActive={searchActive}
              loadingIds={loadingIds}
              failedChildIds={failedChildIds}
              // #750: the manual Retry button routes through the recovery hook's wrapper
              // so a user-initiated click resets that branch's auto-retry budget (see
              // useFailedBranchRecovery) — a human stepping in should not be capped by
              // the same limit that exists to stop an unattended retry loop.
              onRetryChildren={manualRetryChildren}
              onLoadChildren={loadChildren}
              onLoadMore={loadMore}
              onSelectedChange={(ids) => handleSelectedChange(null, ids)}
              onExpandedChange={(ids) => handleExpandedChange(null, ids)}
              onItemClick={handleItemClick}
              onContextMenu={(event, item) => handleContextMenu(event, item.id, item)}
              onMoveItem={moveItem}
              onRename={(id, title) => requirementsApi.getItem(id)
                // #572: PUT now requires a `version` precondition — fetch the item's
                // current version right before the rename (the tree row itself doesn't
                // carry a fresh-enough one to trust for a write).
                // #579: this chain had NO catch, so a 409 rename was an unhandled
                // promise rejection — the row silently snapped back to the old title
                // with nothing said. Route it through the reload / keep-mine prompt;
                // refreshTree() then shows whichever version won.
                .then((current) => saveWithConflictPrompt(
                  id, { title, version: current.version },
                  { label: current.display_id || current.title || id }))
                .then(() => refreshTree())}
              onBackgroundContextMenu={(e) => setContextMenu({
                mouseX: e.clientX, mouseY: e.clientY, id: null, item: null, selectedItems: [], isBackground: true,
              })}
            />
            )}
          </div>
        ) : null}

        {/* Resizer: tree | detail (desktop only; hidden on mobile via CSS). */}
        {treeVisible && !treeOverlay && (
          <div className="col-resizer" onMouseDown={startColResize('tree')}
            onKeyDown={resizeColByKey('tree')}
            role="slider" tabIndex={0} aria-orientation="vertical"
            aria-valuenow={treeW} aria-valuemin={COL_BOUNDS.tree.min} aria-valuemax={COL_BOUNDS.tree.max}
            aria-label="Resize requirements panel" />
        )}

        {/* DETAIL COLUMN (mockup .detail) — overlays full-screen on mobile when
            an item is selected (the tree is the default mobile view). */}
        <div className={`detail${detailsItemId ? ' detail-overlay' : ''}`}>
          {detailsItemId ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {!treeVisible && (
                  <button type="button"
                    className="btn ghost sm tree-summon-btn"
                    title="Show requirements tree"
                    aria-label="Show requirements tree"
                    onClick={() => setTreeOverlay((o) => !o)}
                  >
                    <Ic name="panel" /> Tree
                  </button>
                )}
                <button type="button" className="btn ghost sm m-back" onClick={() => setSelected([])} aria-label="Back to list">
                  <Ic name="chev" style={{ transform: 'rotate(180deg)' }} /> Back
                </button>
              </div>
              <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                {/* #351: a selected PROJECT node shows the project overview + breakdown
                    (project node ids are `project:<pid>`), not the item detail. */}
                {String(detailsItemId).startsWith('project:') ? (
                  <ProjectDetails
                    projectId={String(detailsItemId).slice('project:'.length)}
                    onNavigateToItem={(id) => setSelected([id])}
                    refreshTree={refreshTree}
                  />
                ) : (
                  <ItemDetails
                    selectedItemId={detailsItemId}
                    refreshTree={refreshTree}
                    // #567: optimistic single-node update/remove for Save/Delete —
                    // replaces the unconditional refreshTree() those used to call.
                    patchNode={patchNode}
                    removeNode={removeNode}
                    // #569: Approve/Unapprove updates the tree's approval-state map
                    // directly — no tree/network refetch of any kind.
                    onApprovalChange={handleApprovalChange}
                    // #342: the Breakdown tab's id-chip deep-link — reuses the
                    // same "select just this item" mechanism as the context
                    // menu's Edit action above.
                    onNavigateToItem={(id) => setSelected([id])}
                    // #817: Prev/Next follow the tree's VISIBLE order with active
                    // filters. Nulls at the ends drive the disabled state.
                    prevItemId={itemNav.prevId}
                    nextItemId={itemNav.nextId}
                  />
                )}
              </div>
            </>
          ) : selected.length > 1 ? (
            <div className="dbody">
              <div className="t-sec">{selected.length} items selected</div>
              <div className="t-body muted">Select a single item to view details.</div>
            </div>
          ) : isMobile ? (
            // #440: the analytics dashboard is desktop-only on the home view — on the
            // narrow mobile screen the home page IS the tree, so mounting the dashboard
            // (and firing its aggregate fetch) would be wasted work behind the tree.
            <div className="dbody" data-testid="home-empty-mobile">
              {!treeVisible && (
                <button type="button"
                  className="btn ghost sm tree-summon-btn"
                  title="Show requirements tree"
                  aria-label="Show requirements tree"
                  style={{ alignSelf: 'flex-start', marginBottom: 8 }}
                  onClick={() => setTreeOverlay((o) => !o)}
                >
                  <Ic name="panel" /> Tree
                </button>
              )}
              <div className="t-body muted" style={{ marginBottom: 4 }}>Select an item to view details.</div>
            </div>
          ) : (
            // #440: the rationalised analytics dashboard (DashboardHome) is the home
            // "nothing selected" view on desktop — it reads ONE bounded aggregate
            // (/items/summary), replacing the retired Coverage/Quality/Activity panels.
            <div className="dbody" data-testid="home-dashboard" style={{ padding: 0, minHeight: 0 }}>
              <DashboardHome />
            </div>
          )}
        </div>

        {/* Resizer: detail | assistant (desktop only). */}
        {chatPanelVisible && !mobileAiOpen && (
          <div className="col-resizer" onMouseDown={startColResize('ai')}
            onKeyDown={resizeColByKey('ai')}
            role="slider" tabIndex={0} aria-orientation="vertical"
            aria-valuenow={aiW} aria-valuemin={COL_BOUNDS.ai.min} aria-valuemax={COL_BOUNDS.ai.max}
            aria-label="Resize assistant panel" />
        )}

        {/* ASSISTANT COLUMN (mockup .aicol) — overlays full-screen on mobile when
            opened via the FAB. */}
        {(chatPanelVisible || mobileAiOpen) && (
          <div className={`aicol${mobileAiOpen ? ' ai-overlay' : ''}`}
            style={!mobileAiOpen ? { flex: `0 0 ${aiW}px`, width: aiW } : undefined}>
            {/* ChatInterface renders a position:fixed root, so the close button
                must be fixed above it (z above the overlay) to stay tappable.
                #627 (adversarial review F4): this was a hardcoded `2000`, i.e.
                above MUI's modal layer (1300) — so with the assistant open, any
                dialog (Cmd/Ctrl+K palette, conflict prompt, AddDialog) had this
                button painting over its top-left corner and eating pointer events
                there. Same defect as the item-card layer, same fix: take the value
                from the one stacking scale (`utils/zLayers.js`), which keeps it
                above the chat root and strictly below any dialog. */}
            <button type="button"
              className="btn secondary sm m-back"
              style={{ position: "fixed", top: 53, left: 8, zIndex: Z_MOBILE_ASSISTANT_BACK }}
              onClick={() => setMobileAiOpen(false)}
              aria-label="Close assistant"
            >
              <Ic name="chev" style={{ transform: 'rotate(180deg)' }} /> Back
            </button>
            <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
              <ChatInterface
                context={chatContext}
                height="100%"
                initialOpen={true}
                requirements={flatRequirements}
                onResponse={() => refreshTree()}
                onMinimize={handleChatMinimize}
              />
            </div>
          </div>
        )}

        {/* #1032: desktop re-open affordance when the assistant panel is closed.
            The panel is closed by default and minimizing now fully unmounts
            `.aicol` (0 width) rather than leaving an empty column, so this is
            the ONLY discoverable way back in on desktop — mobile already has
            its own always-rendered `.fab` below (screens.css shows it only
            <640px, so the two never appear together). Plain MUI (position:
            fixed + a breakpoint `display`) rather than a screens.css class, so
            it doesn't need a stylesheet change. */}
        {!chatPanelVisible && !mobileAiOpen && (
          <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000, display: { xs: 'none', md: 'flex' } }}>
            <Tooltip title="Open AI assistant panel">
              <IconButton
                color="primary"
                size="large"
                data-testid="assistant-reopen-btn"
                aria-label="Open AI assistant panel"
                onClick={() => { setChatPanelVisible(true); setPref('assistantPanel', true); }}
                sx={{
                  bgcolor: 'background.paper',
                  boxShadow: 2,
                  '&:hover': { bgcolor: 'background.paper', boxShadow: 3 },
                }}
              >
                <Ic name="spark" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* MOBILE FAB — open the assistant (shown < 640px via screens.css) */}
        <button type="button" className="fab" aria-label="Open assistant" onClick={() => setMobileAiOpen(true)}>
          <Ic name="spark" />
        </button>
      </div>


      {/* Context menu with enhanced props */}
      {contextMenu && (
        <ContextMenu
          anchorPosition={contextMenu}
          onClose={handleContextMenuClose}
          onAction={handleContextMenuAction}
          item={contextMenu.item}
          // #648: "Move up"/"Move down" are hidden-via-disable on the first/last
          // sibling respectively — getSiblingOrder walks the SAME items tree
          // planMoveSibling reads at click time.
          disabledActions={(() => {
            if (!contextMenu.item || contextMenu.id == null) return [];
            const info = getSiblingOrder(items, contextMenu.id);
            if (!info) return ['moveUp', 'moveDown'];
            const disabled = [];
            if (info.index <= 0) disabled.push('moveUp');
            if (info.index >= info.siblingIds.length - 1) disabled.push('moveDown');
            return disabled;
          })()}
          additionalActions={[
            { 
              id: 'chat', 
              label: 'Discuss with AI', 
              icon: 'chat' 
            }
          ]}
        />
      )}

      {/* Bulk AI operations dialog (#114) */}
      <BulkAiDialog
        open={Boolean(bulkAiTarget)}
        onClose={() => setBulkAiTarget(null)}
        containerId={bulkAiTarget?.id}
        containerTitle={bulkAiTarget?.title}
      />

      {/* Delete multiple items dialog */}
      <DeleteMultipleDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteMultipleItems}
        items={itemsToDelete}
      />

      {/* Feedback snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* AddDialog for creating new items/containers */}
      <AddDialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        onAdd={handleAddDialogSubmit}
        parentId={addDialogParentId}
        isContainer={addDialogIsContainer}
        projectId={addDialogProjectId}
        parentPath={addDialogParentPath}
      />

      {/* #408: "Move…" context-menu action — pick a new parent container from the
          tree, scoped to the item's own project (a move can't cross projects). */}
      {moveTarget && (
        <UnifiedContainerSelector
          open
          title={`Move "${moveTarget.item?.title || 'item'}"`}
          selectMode="containers"
          projectId={moveTarget.item?.projectId || moveTarget.item?.project_id || null}
          // #408: pre-open to the item's current location (its ancestor chain).
          initialExpandedIds={(findAncestorIds(items, moveTarget.id) || []).filter((x) => x !== moveTarget.id)}
          onClose={() => setMoveTarget(null)}
          onSelect={(destId) => {
            if (destId && destId !== moveTarget.id) {
              moveItem(moveTarget.id, { new_parent_id: destId });
            }
            setMoveTarget(null);
          }}
        />
      )}

      {/* #275/#287: "New Project" (tree "+", or a project's own right-click menu)
          and "Copy Project" (project right-click menu) share this one dialog.
          Both land at the tree's top level, so both need BOTH
          `reloadMyProjects` (the project LIST itself only re-fetches on
          `activeOrg` change — see useTreeState.js) and `refreshTree` (the
          items/roots) or the new/copied project silently wouldn't appear until
          a full page reload. */}
      {projectDialog && (
        <NewProjectDialog
          open
          title={projectDialog.mode === 'copy' ? `Copy "${projectDialog.name}"` : 'New project'}
          submitLabel={projectDialog.mode === 'copy' ? 'Copy' : 'Create'}
          onClose={() => setProjectDialog(null)}
          onSubmit={(name) => (
            projectDialog.mode === 'copy'
              ? iamApi.copyProject(projectDialog.pid, { name, mode: 'full' })
              : iamApi.createProject(activeOrg, name)
          )}
          onDone={async (result) => {
            setProjectDialog(null);
            await reloadMyProjects();
            refreshTree();
            showSnackbar(`Project "${result.name}" ${projectDialog.mode === 'copy' ? 'copied' : 'created'}`, 'success');
          }}
        />
      )}

      {/* #195: "Archive Project…" from the tree's project context menu — same
          typed-confirmation dialog Settings → Organisation → Projects uses
          (`ProjectDangerDialog`), so archiving is reachable from the tree
          itself instead of only from Settings. */}
      <ProjectDangerDialog
        open={!!archiveProjectTarget}
        variant="archive"
        projectName={archiveProjectTarget?.name}
        onClose={() => setArchiveProjectTarget(null)}
        onConfirm={() => {
          const target = archiveProjectTarget;
          setArchiveProjectTarget(null);
          iamApi.archiveProject(target.id)
            .then(async () => {
              await reloadMyProjects();
              refreshTree();
              showSnackbar(`Project "${target.name}" archived`, 'success');
            })
            .catch((err) => showSnackbar(`Archive failed: ${err?.response?.data?.detail || err.message}`, 'error'));
        }}
      />

      {/* #943: in-app confirmation modal, replacing the native window.confirm the
          unsaved-changes navigation guard used. */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) c.onConfirm(); }}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        severity={confirm?.severity || null}
        confirmColor={confirm?.confirmColor || 'primary'}
        confirmLabel={confirm?.confirmLabel || 'Confirm'}
        testId={confirm?.testId || 'tree-confirm-dialog'}
      />
    </TreeViewContext.Provider>
  );
}
